/**
 * ContagensCardView - Card grid view for ContagensPage
 *
 * Extracted from ContagensPage to keep it under 500 lines.
 */
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { FileText, Eye, Trash2, RotateCcw } from 'lucide-react';
import { formatDateBR } from '../../utils/formatters';
import { getStatusColor, getStatusLabel } from '../../constants/status';
import PermissionGate from '../PermissionGate';

interface ContagensCardViewProps {
  jobs: any[];
  onDelete: (id: number) => void;
  // N217: aba Excluidas -- sem isto o card so oferecia Deletar (400 "ja foi deletado")
  onRestore?: (id: number) => void;
}

export const ContagensCardView: React.FC<ContagensCardViewProps> = ({ jobs, onDelete, onRestore }) => {
  const navigate = useNavigate();

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      {jobs.map(job => (
        <div
          key={job.id}
          className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6 hover:shadow-lg transition-all duration-200"
        >
          <div className="flex items-start justify-between mb-4">
            <div className="p-3 bg-blue-50 dark:bg-blue-900/30 rounded-lg">
              <FileText className="w-6 h-6 text-blue-600 dark:text-blue-400" />
            </div>
            <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-semibold border ${getStatusColor(job.status)}`}>
              {getStatusLabel(job.status)}
            </span>
          </div>

          <p className="text-sm font-medium text-gray-900 dark:text-white mb-1">{job.hospital_nome}</p>
          <p className="text-xs text-gray-500 dark:text-gray-500 mb-4">{job.location_nome}</p>

          <div className="space-y-3 mb-4">
            <div className="flex justify-between text-sm">
              <span className="text-gray-600 dark:text-gray-400">Auditores:</span>
              <span
                className="font-medium text-gray-900 dark:text-white truncate max-w-[150px]"
                title={job.auditores?.map((a: any) => a.nome).join(', ') || job.operador_nome}
              >
                {job.auditores?.map((a: any) => a.nome.split(' ')[0]).join(', ') || job.operador_nome?.split(' ')[0]}
              </span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-600 dark:text-gray-400">Data Prevista:</span>
              <span className="font-medium text-gray-900 dark:text-white">{formatDateBR(job.data_prevista)}</span>
            </div>
            <div>
              <div className="flex justify-between text-sm mb-1">
                <span className="text-gray-600 dark:text-gray-400">Meta:</span>
                <span className="font-bold text-blue-600 dark:text-blue-400">{job.total_items_expected || '-'}</span>
              </div>
              {job.total_items_expected > 0 && (
                <>
                  <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                    <div
                      className="bg-blue-600 h-2 rounded-full"
                      style={{ width: `${Math.min(100, (job.total_items_counted / job.total_items_expected) * 100)}%` }}
                    />
                  </div>
                  <div className="text-xs text-right text-gray-500 mt-0.5">
                    {Math.round((job.total_items_counted / job.total_items_expected) * 100)}% concluído
                  </div>
                </>
              )}
            </div>
          </div>

          <div className="flex gap-2">
            <button
              onClick={() => navigate(`/cloud/contagens/${job.id}`)}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-600 dark:text-gray-400 rounded transition-colors"
              title="Ver detalhes"
            >
              <Eye className="w-4 h-4" />
            </button>
            {onRestore && !job.ativo ? (
              <PermissionGate tela="CONTAGENS_LIST" acao="editar">
                <button
                  onClick={() => onRestore(job.id)}
                  className="p-2 hover:bg-green-50 dark:hover:bg-green-900/30 text-green-600 dark:text-green-400 rounded transition-colors"
                  title="Restaurar"
                >
                  <RotateCcw className="w-4 h-4" />
                </button>
              </PermissionGate>
            ) : (
              <PermissionGate tela="CONTAGENS_LIST" acao="excluir">
                <button
                  onClick={() => onDelete(job.id)}
                  className="p-2 hover:bg-red-50 dark:hover:bg-red-900/30 text-red-600 dark:text-red-400 rounded transition-colors"
                  title="Deletar"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </PermissionGate>
            )}
          </div>
        </div>
      ))}
    </div>
  );
};
